import { Injectable, ElementRef } from '@angular/core';
import { filter, first } from 'rxjs/operators';

import { MediaResolved } from 'idt-ecommerce-api/catalog/products/medias/medias.models';
import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { StoreDetail } from 'idt-ecommerce-api/stores/stores.models';
import { RenderConfiguration } from 'render/render.configuration';
import { LazyDataInterface } from 'shared/lazy-load/lazy-load.interface';


export interface AmandaOptions {
  isVideoPoster?: boolean;
  isWidthParamEnabled: boolean;
  width?: number | null;
}

@Injectable({ providedIn: 'root' })
export class AssetsSharedService {

  private timestamp?: string;

  public static isAmandaURL(assetURL: string): boolean {
    return assetURL.includes('/assets/public/');
  }

  constructor(private readonly contextService: ContextService) {
    this.contextService.getStore$()
      .pipe(
        filter(
          (storeInfo: StoreDetail) => !!storeInfo && !!storeInfo.details && !!storeInfo.details.timestampBuildVersion
        ),
        first()
      )
      .subscribe((storeInfo: StoreDetail) => {
        this.timestamp = storeInfo.details.timestampBuildVersion;
      });
  }

  public getAssetUrl(
    asset: string | undefined,
    assetsHostUrl: string | undefined,
    options?: AmandaOptions
  ): string | undefined {
    let url = asset ? this.getAbsoluteAssetUrl(asset, assetsHostUrl) : undefined;

    if (options && url) {
      this.setVideoPosterOption(url, options);
    }

    if (url) {
      url = this.addAmandaParamsIfNeeded(url, options);
      url = this.addTimestampParamsIfNeeded(url);
    }

    return url;
  }

  /**
   * Returns the width for the DOM element that will contain the image
   * @param elRef Reference to the DOM element
   */
  public getWidth(elRef?: ElementRef): number {
    const width: number = elRef?.nativeElement?.offsetWidth;
    return width;
  }

  /**
   * Returns product image data (as LazyDataInterface). Also includes w Amanda param
   */
  public productImageData(
    media: MediaResolved | undefined,
    width: number,
    config: RenderConfiguration
  ): LazyDataInterface | undefined {
    let productImageData: LazyDataInterface | undefined;

    if (isDefined(media)) {
      const isWidthParamEnabled = config.amandaImageVideoManagerEnabled;

      const options: AmandaOptions = { isWidthParamEnabled, width };
      const src = this.getAssetUrl(media.extraInfo?.deliveryUrl || media.path, config.assetsUrl, options);
      productImageData = { src, width: media.size?.width, height: media.size?.height };
    }

    return productImageData;
  }

  /**
   * Returns true if is a path of HLS video (.m3u8 extension)
   * @param path file path or url
   */
  public isHLSVideo(path: string) {
    return path.endsWith('.m3u8') || path.includes('.m3u8?');
  }

  /**
   * @param {number} ih : image height
   * @param {number} iw : image width
   * @param {number} wh : window height
   * @param {number} ww : window width
   * @param {number} [density] : density window
   * @return {number} Optimum image width
   * @public
   */
  public getOptimumImageWidth(
    ih: number,
    iw: number,
    wh: number,
    ww: number,
    density?: number
  ): number {
    let result: number = 0;

    if (!density) {
      density = window.devicePixelRatio;
    }

    if (ih <= wh || iw <= ww) {
      return Math.ceil(iw);
    }

    if (
      this.calculateSideRelation(ih, wh) <= this.calculateSideRelation(iw, ww)
    ) {
      result = Math.ceil(iw * this.calculateSideRelation(iw, ww) * density);
      return (result <= iw) || (Math.ceil(ih * this.calculateSideRelation(iw, ww) * density) <= ih) ? result : iw;
    }

    result = Math.ceil(iw * this.calculateSideRelation(ih, wh) * density);
    return (result <= iw) || (Math.ceil(ih * this.calculateSideRelation(ih, wh) * density) <= ih) ? result : iw;
  }

  // ---- Private functions ----

  // Sets the isVideoPoster option flag, true if the image is a poster of a Amanda Video
  private setVideoPosterOption(url: string, options: AmandaOptions): void {
    if (options.isVideoPoster === undefined) {
      options.isVideoPoster = url.includes('/poster.');
      // Posters of amanda videos do not accept the W parameter
    }
  }

  /**
   * @param {number} imageSize : height or width of image
   * @param {number} windowSize : height or width of window
   * @return {number}
   */
  private calculateSideRelation(imageSize: number, windowSize: number): number {
    return windowSize / imageSize;
  }

  private getAbsoluteAssetUrl(asset: string, assetsHostUrl: string | undefined): string {
    let fullUrl: string = asset.replace(/\\/g, '/'); // replace Windows backslash for forward slash;

    if (this.isRelativePath(fullUrl) && assetsHostUrl) {
      const separator = assetsHostUrl.endsWith('/') ? '' : '/';
      fullUrl = assetsHostUrl + separator + asset;
    }

    return fullUrl;
  }

  private isRelativePath(path: string) {
    return !path.startsWith('http');
  }

  private addAmandaParamsIfNeeded(assetUrl: string, options?: AmandaOptions): string {
    if (AssetsSharedService.isAmandaURL(assetUrl)) {
      let assetUrlWithParams = assetUrl;
      if (this.isWParamSupported(assetUrlWithParams)
        && !assetUrlWithParams.includes('w=')
        && options
        && (options.isWidthParamEnabled === undefined || options.isWidthParamEnabled)
        && !options?.isVideoPoster
      ) {
        const width = this.getStandardWidth(options.width);
        assetUrlWithParams = assetUrlWithParams.includes('?') ? `${assetUrlWithParams}&w=${width}` : `${assetUrlWithParams}?w=${width}`;
      }
      if ((assetUrlWithParams.includes('.jpg') || assetUrlWithParams.includes('.jpeg'))
        && !assetUrlWithParams.includes('f=') && !options?.isVideoPoster) {
        return assetUrlWithParams.includes('?') ? `${assetUrlWithParams}&f=auto` : `${assetUrlWithParams}?f=auto`;
      }

      return assetUrlWithParams;
    }
    return assetUrl;
  }

  private addTimestampParamsIfNeeded(assetUrl: string): string | undefined {
    if (!assetUrl.match(/(\?|&)((ts=)|t=)/) && this.timestamp) {
      assetUrl = assetUrl.includes('?') ? `${assetUrl}&ts=${this.timestamp}` : `${assetUrl}?ts=${this.timestamp}`;
    }

    return assetUrl;
  }

  private getStandardWidth(width?: number | null): number {
    const windowWidthFallback = window.innerWidth;
    const defaultWidthFallback = 800;

    if (!width || width === 0) { // fallbacks
      width = windowWidthFallback;

      if (width <= 0) {
        width = defaultWidthFallback;
      }
    }

    const density = window.devicePixelRatio > 3 ? 3 : window.devicePixelRatio;
    const standardWidth = width * density;
    return standardWidth;
  }

  private isWParamSupported(assetUrl: string): boolean {
    return !['.svg', '.gif', '.pdf'].some((extension) => assetUrl.includes(extension));
  }

}
